import React, { useEffect, useRef, useState } from "react";
import ModalMotionShell from "./ModalMotionShell";
import ModalDragHandle from "./ModalDragHandle";
import {
  ArrowDownUp,
  ChevronRight,
  FileText,
  Info,
  ListChecks,
  X,
  Zap,
  Wallet,
  Upload,
  FileDown,
} from "lucide-react";

function useDraggable() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => {
      setPos({
        x: ev.clientX - startX,
        y: ev.clientY - startY,
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return { ref, pos, onMouseDown, setPos };
}

function useIsHandheldModal(open) {
  const [isHandheld, setIsHandheld] = useState(false);

  useEffect(() => {
    if (!open) return;

    const mediaQuery = window.matchMedia?.("(pointer: coarse)");

    const compute = () => {
      const isSmall = window.innerWidth < 768;
      setIsHandheld(Boolean(mediaQuery?.matches && isSmall));
    };

    compute();

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener("change", compute);
    } else if (mediaQuery?.addListener) {
      mediaQuery.addListener(compute);
    }

    window.addEventListener("resize", compute);

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener("change", compute);
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(compute);
      }
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  return isHandheld;
}

function StatusPill({ children, tone = "gray" }) {
  const cls =
    tone === "blue"
      ? "bg-blue-50 text-blue-700 border-blue-200"
      : tone === "indigo"
      ? "bg-indigo-50 text-indigo-700 border-indigo-200"
      : "bg-gray-50 text-gray-700 border-gray-200";

  return (
    <span className={`text-xs px-2.5 py-1 rounded-full border ${cls} whitespace-nowrap`}>
      {children}
    </span>
  );
}

function IconBadge({ icon: Icon, tone = "blue" }) {
  const toneClass =
    tone === "blue"
      ? "bg-blue-50 border-blue-100 text-blue-700"
      : tone === "indigo"
      ? "bg-indigo-50 border-indigo-100 text-indigo-700"
      : "bg-gray-50 border-gray-200 text-gray-700";

  return (
    <div
      className={`relative w-10 h-10 rounded-xl border ${toneClass} flex items-center justify-center shadow-sm transition-all duration-200
      group-hover:shadow-md group-hover:border-blue-200`}
      aria-hidden="true"
    >
      {/* Glow */}
      <span
        className="
          absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300
          bg-gradient-to-br from-blue-400/20 via-indigo-400/15 to-transparent blur-md
        "
      />
      <Icon
        className="
          relative w-[18px] h-[18px]
          transition-transform duration-300 will-change-transform
          group-hover:rotate-6 group-hover:scale-110
          group-hover:animate-[iconFloat_650ms_ease-in-out_infinite_alternate]
        "
      />
    </div>
  );
}

function InfoCallout({ title, text, hint }) {
  return (
    <div className="group rounded-2xl border border-blue-200 bg-blue-50/60 p-4 transition-all duration-200 hover:bg-blue-50 hover:border-blue-300 hover:shadow-sm">
      <div className="flex items-start gap-3">
        <div className="mt-0.5">
          <IconBadge icon={Info} tone="blue" />
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-sm text-gray-700 mt-1 leading-relaxed whitespace-pre-line">{text}</div>
          {hint ? (
            <div className="mt-2 text-xs text-gray-600">
              <span className="font-medium text-gray-900">{hint.label}:</span>{" "}
              <span className="font-mono bg-white/70 border border-blue-100 rounded px-2 py-0.5">
                {hint.value}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StepRow({ steps }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
      {steps.map((s, idx) => (
        <div
          key={idx}
          className="
            group relative bg-white border border-gray-200 rounded-2xl p-3.5 shadow-sm
            transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 hover:border-blue-200
          "
        >
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <IconBadge icon={s.icon} tone={s.tone || "blue"} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 truncate">{s.title}</div>
              <div className="text-xs text-gray-500 mt-0.5 leading-snug">{s.subtitle}</div>
            </div>
          </div>

          {/* subtle connector hint */}
          {idx < steps.length - 1 ? (
            <div className="hidden sm:block pointer-events-none absolute -right-2 top-1/2 -translate-y-1/2 text-gray-200">
              <ChevronRight className="w-5 h-5" />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function FeatureCard({ icon, title, badge, badgeTone, body, note, language }) {
  const overviewText = language === "en" ? "Overview" : "Kurzüberblick";
  const pointsText = language === "en" ? "points" : "Punkte";

  return (
    <div className="relative group h-full bg-white border border-gray-200 rounded-2xl p-5 shadow-sm transition-all duration-200 hover:shadow-md hover:border-blue-200 hover:-translate-y-0.5">
      <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-indigo-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <div className="absolute top-4 right-4 flex items-center gap-2 pointer-events-none" aria-hidden="true">
        <StatusPill tone={badgeTone || "gray"}>{badge}</StatusPill>
        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-300 transition-transform duration-200 group-hover:translate-x-0.5" />
      </div>

      <div className="relative flex items-start justify-between gap-3 pr-32">
        <div className="flex items-start gap-3 min-w-0">
          <div className="mt-0.5">
            <IconBadge icon={icon} tone={badgeTone === "indigo" ? "indigo" : "blue"} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate">{title}</h3>
            <div className="text-xs text-gray-500 mt-1">
              {body?.length ? `${body.length} ${pointsText} • ${overviewText}` : overviewText}
            </div>
          </div>
        </div>
      </div>

      <ul className="relative mt-4 space-y-2.5">
        {body.map((row, rIdx) => (
          <li key={rIdx} className="flex gap-3 text-sm text-gray-700 leading-relaxed">
            <span
              className="
                mt-[9px] h-1.5 w-1.5 rounded-full bg-gray-300 flex-shrink-0
                transition-colors duration-200 group-hover:bg-blue-400
              "
              aria-hidden="true"
            />
            <div className="min-w-0">
              <span className="font-medium text-gray-900">{row.label}</span>
              <span className="text-gray-400"> — </span>
              <span className="whitespace-pre-line">{row.text}</span>
            </div>
          </li>
        ))}
      </ul>

      {note ? (
        <div className="relative mt-4 rounded-xl bg-gray-50 border border-gray-200 px-3 py-2 text-xs text-gray-600 leading-relaxed whitespace-pre-line">
          {note}
        </div>
      ) : null}
    </div>
  );
}

export default function HowV3TaxWorksModal({ open, onClose, language }) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const drag = useDraggable();
  const isHandheld = useIsHandheldModal(open);

  useEffect(() => {
    if (open) drag.setPos({ x: 0, y: 0 });
  }, [open]);

  const t = {
    de: {
      title: "Wie V3Tax funktioniert",
      subtitle: "Berechnung & Abdeckung im Überblick",
      intro1:
        "V3Tax analysiert Ihre VeChain-Wallets und berechnet steuerlich relevante Transaktionen automatisch — nachvollziehbar und konsistent.",
      intro2:
        "On-chain Daten (Wallet) + optional Off-chain Daten Börsen-CSV (Empfohlen) = vollständige Abdeckung für Swaps, NFTs und Preise.",
      steps: [
        { icon: Wallet, title: "Wallets", subtitle: "Verbinden & Set anlegen", tone: "blue" },
        { icon: Upload, title: "CSV", subtitle: "Börsen-Trades importieren", tone: "blue" },
        { icon: ArrowDownUp, title: "FIFO", subtitle: "Lots & Steuerlogik", tone: "indigo" },
        { icon: FileDown, title: "Export", subtitle: "PDF/CSV Reports", tone: "indigo" },
      ],
      calloutTitle: "Wichtig: Börsen-CSV für Swaps & NFT-Trades",
      calloutText:
        "Zur Berechnung von Swaps und NFT-Trades ist ein CSV-Import notwendig um Einstandspreise und Haltedauern korrekt berechnen zu können.\n\n– Ohne CSV Import fehlt die Grundlage zur korrekten Berechnung.",
      calloutHint: { label: "Upload-Pfad", value: "Meine Wallets → Börsen-Imports (CSV)" },
      sections: [
        {
          icon: ListChecks,
          title: "Transaktionen automatisch klassifizieren",
          badge: "Classifier",
          badgeTone: "blue",
          body: [
            { label: "Swaps", text: "DEX- und vechain.energy-Swaps werden als Swap in / Swap out erkannt." },
            { label: "NFTs", text: "Mints, Käufe und Verkäufe werden mit Zahlungsfluss und Kostenbasis verknüpft." },
            { label: "Rewards", text: "Staking-, Governance- und X-2-Earn-Rewards werden als Zuflüsse klassifiziert." },
          ],
          note: "Beispiele:\n\nStargate Staking Rewards, Thorhead Staking Rewards,\nGovernance Voting Reward, X2earn Rewards, etc.",
        },
        {
          icon: ArrowDownUp,
          title: "FIFO-Steuerberechnung (nachvollziehbar)",
          badge: "FIFO Engine",
          badgeTone: "indigo",
          body: [
            { label: "Matching", text: "Veräußerungen werden lot-genau gegen frühere Zuflüsse gematcht." },
            { label: "Haltefristen", text: "Haltedauer und steuerpflichtige Gewinne werden regelwerksabhängig berechnet." },
            {
              label: "Datenquellen",
              text:
                "Transfers: On-chain-Transferhistorie über VeChain-Datenquellen.\n" +
                "Preise: Mehrstufiges Quellenmodell mit Tier-1-Marktdaten für breit gelistete Assets, historischen DEX-Daten für unterstützte VeChain-Assets und ergänzenden Ökosystem-Preisquellen für ausgewählte VeWorld-Tokens.",
            },
            {
              label: "Preislogik",
              text:
                "V3Tax verwendet für die automatische Steuerberechnung nur Token-Preise innerhalb definierter Qualitäts- und Plausibilitätsgrenzen. Je nach Quelle werden exakte oder nahe Kursstützpunkte, Interpolation oder geprüfte Tages-/DEX-Daten genutzt.\n" +
                "Wenn für eine Transaktion kein ausreichend verlässlicher Token-Preis verfügbar ist, wird diese Transaktion wegen nicht belastbarem Token-Preis von der Berechnung ausgeschlossen.",
            },
          ],
        },
        {
          icon: Zap,
          title: "VeChain-spezifische Abdeckung",
          badge: "Chain Coverage",
          badgeTone: "blue",
          body: [
            {
              label: "B3TR / VOT3",
              text: "B3TR → VOT3 wird als lot-erhaltender Wrap-/Governance-Lock modelliert.\nKostenbasis und Haltedauer bleiben erhalten.",
            },
            {
              label: "Stargate",
              text: "Deposits und Withdrawals werden als Lock-/Unlock-Vorgänge FIFO-neutral abgebildet.\nStargate Staking Rewards separat als VTHO-Zuflüsse.",
            },
            {
              label: "Galaxy Upgrades",
              text: "VeBetterDAO Galaxy Upgrades werden als NFT Galaxy Mint klassifiziert\nund mit zugehörigem B3TR-Zahlungsfluss in FIFO berücksichtigt.",
            },
            {
              label: "VTHO / Gas Fees",
              text: "Vor Hayabusa wird VTHO aus dem historischen VET-Bestand rekonstruiert; Gas Fees werden per Receipt dem Gas Payer zugeordnet und bestandsmindernd verbucht.",
            },
          ],
        },
        {
          icon: FileText,
          title: "Reports & Exporte",
          badge: "Exports",
          badgeTone: "indigo",
          body: [
            {
              label: "CSV-Reports",
              text: "Klassifiziertes Ledger (On-chain-Transfers + rekonstruierte Protokoll-Events) sowie finale Reports für Veräußerungsgeschäfte, sonstige Einkünfte und Tax-Lot-Audit.",
            },
            {
              label: "PDF Appendix 1",
              text: "Gesamtbericht mit allen berechneten Steuertabellen, Summen und Reportwerten.",
            },
            {
              label: "PDF Appendix 2",
              text: "Separater Report über das klassifizierte Ledger (On-chain-Transfers + rekonstruierte Protokoll-Events).",
            },
            {
              label: "DE / EN",
              text: "Ausgaben in Deutsch und Englisch für Prüfung, Dokumentation und Steuerberater-Workflows.",
            },
          ],
        },
      ],
      close: "Schließen",
    },

    en: {
      title: "How V3Tax works",
      subtitle: "Calculation & coverage overview",
      intro1:
        "V3Tax analyzes your VeChain wallets and calculates tax-relevant transactions automatically — consistent and traceable.",
      intro2:
        "On-chain (wallet) + optional off-chain (exchange CSV) = full coverage for swaps, NFTs and pricing details.",
      steps: [
        { icon: Wallet, title: "Wallets", subtitle: "Connect & build a set", tone: "blue" },
        { icon: Upload, title: "CSV", subtitle: "Import exchange trades", tone: "blue" },
        { icon: ArrowDownUp, title: "FIFO", subtitle: "Lots & tax logic", tone: "indigo" },
        { icon: FileDown, title: "Export", subtitle: "PDF/CSV reports", tone: "indigo" },
      ],
      calloutTitle: "Important: Exchange CSV for swaps & NFT trades",
      calloutText:
        "If trading happens off-wallet (or price details only exist on the exchange), CSV imports are required — otherwise parts of the valuation may be missing.",
      calloutHint: { label: "Upload path", value: "My Wallets → Exchange imports (CSV)" },
      sections: [
        {
          icon: ListChecks,
          title: "Automatic transaction classification",
          badge: "Classifier",
          badgeTone: "blue",
          body: [
            { label: "Swaps", text: "DEX and vechain.energy swaps are recognized as swap in / swap out." },
            { label: "NFTs", text: "Mints, buys and sells are linked with payment flow and cost basis." },
            { label: "Rewards", text: "Staking, governance and X-2-Earn rewards are classified as inflows." },
          ],
          note: "Examples:\n\nStargate Staking Rewards, Thorhead Staking Rewards,\nGovernance Voting Reward, X2earn Rewards, etc.",
        },
        {
          icon: ArrowDownUp,
          title: "FIFO-based tax calculation (traceable)",
          badge: "FIFO Engine",
          badgeTone: "indigo",
          body: [
            { label: "Matching", text: "Disposals are matched lot-accurately against earlier inflows." },
            { label: "Holding periods", text: "Holding period and taxable gains are calculated depending on the rule set." },
            {
              label: "Data sources",
              text:
                "Transfers: on-chain transfer history from VeChain data sources.\n" +
                "Prices: multi-source pricing model using Tier-1 market data for broadly listed assets, historical DEX data for supported VeChain assets, and supplemental ecosystem pricing sources for selected VeWorld tokens.",
            },
            {
              label: "Pricing logic",
              text:
                "V3Tax only uses token prices within defined quality and plausibility limits for automatic tax calculation. Depending on the source, exact or nearby price anchors, interpolation, or validated daily/DEX data may be used.\n" +
                "If no sufficiently reliable token price is available for a transaction, the transaction is excluded from the calculation due to an unreliable token price.",
            },
          ],
        },
        {
          icon: Zap,
          title: "VeChain-specific coverage",
          badge: "Chain Coverage",
          badgeTone: "blue",
          body: [
            {
              label: "B3TR / VOT3",
              text: "B3TR → VOT3 is modeled as a lot-preserving wrap/governance lock.\nCost basis and holding period are retained.",
            },
            {
              label: "Stargate",
              text: "Deposits and withdrawals are shown FIFO-neutrally as lock/unlock.\nStargate staking rewards are recorded separately as VTHO inflows.",
            },
            {
              label: "Galaxy Upgrades",
              text: "VeBetterDAO Galaxy upgrades are classified as NFT Galaxy Mint\nand included in FIFO with the associated B3TR payment flow.",
            },
            {
              label: "VTHO / Gas fees",
              text: "Pre-Hayabusa, VTHO is reconstructed from historical VET balance; gas fees are assigned to the gas payer via receipt and reduce inventory.",
            },
          ],
        },
        {
          icon: FileText,
          title: "Reports & exports",
          badge: "Exports",
          badgeTone: "indigo",
          body: [
            {
              label: "CSV reports",
              text: "Classified ledger data (on-chain transfers + protocol reconstruction events) plus final reports for disposal transactions, other income and tax-lot audit.",
            },
            {
              label: "PDF Appendix 1",
              text: "Complete report with all calculated tax tables, totals and report values.",
            },
            {
              label: "PDF Appendix 2",
              text: "Separate classified ledger report (on-chain transfers + protocol reconstruction events).",
            },
            {
              label: "DE / EN",
              text: "Outputs in German and English for review, documentation and tax-advisor workflows.",
            },
          ],
        },
      ],
      close: "Close",
    },
  };

  const c = t[language] || t.de;

  return (
    <ModalMotionShell
      isOpen={open}
      onClose={onClose}
      stretch={isHandheld}
      backdropClassName={!isHandheld ? "!px-2 !py-2" : ""}
    >
      <style>{`
        @keyframes iconFloat {
          from { transform: translateY(0px); }
          to { transform: translateY(-2px); }
        }
      `}</style>

      <div
        ref={drag.ref}
        style={
          isHandheld
            ? {
                position: "fixed",
                inset: 0,
                height: "100dvh",
                maxHeight: "100dvh",
                zIndex: 10001,
                paddingTop: "max(env(safe-area-inset-top), 12px)",
                paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
              }
            : {
                transform: `translate(${drag.pos.x}px, ${drag.pos.y}px)`,
              }
        }
        className={`bg-white w-full overflow-hidden ${
          isHandheld
            ? "rounded-none shadow-none flex h-full min-h-0 flex-col bg-white"
            : "flex h-[min(99dvh,1220px)] w-[min(99vw,1580px)] max-w-[1580px] flex-col rounded-2xl shadow-2xl"
        }`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="h-[5px] shrink-0 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

        <div
          className={`shrink-0 border-b bg-white ${
            isHandheld ? "px-4 py-3 flex items-start justify-between gap-4" : "px-6 py-3.5 flex items-start justify-between gap-4 cursor-move select-none"
          }`}
          onMouseDown={isHandheld ? undefined : drag.onMouseDown}
        >
          <div className="flex items-center gap-3 min-w-0">
            {!isHandheld && (
              <ModalDragHandle
                onMouseDown={drag.onMouseDown}
                title={language === "en" ? "Move" : "Verschieben"}
                ariaLabel={language === "en" ? "Move modal" : "Modal verschieben"}
                className="mt-0.5 mr-2"
              />
            )}
            <div className="min-w-0">
              <h2 className={`font-semibold text-gray-900 truncate ${isHandheld ? "text-lg" : "text-xl"}`}>
                {c.title}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">{c.subtitle}</p>
            </div>
          </div>

          <button
            onClick={onClose}
            onMouseDown={(e) => e.stopPropagation()}
            className="group text-gray-400 hover:text-gray-600 transition p-2 -m-2 flex-shrink-0"
            aria-label={language === "en" ? "Close" : "Schließen"}
          >
            <X className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
          </button>
        </div>

        <div className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${
          isHandheld ? "px-4 py-4 pb-6" : "px-6 py-4"
        }`}>
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-gray-800 leading-relaxed">{c.intro1}</p>
              <p className="text-sm text-gray-500 leading-relaxed">{c.intro2}</p>
            </div>

            <StepRow steps={c.steps} />

            <InfoCallout title={c.calloutTitle} text={c.calloutText} hint={c.calloutHint} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {c.sections.map((sec, idx) => (
                <FeatureCard
                  key={idx}
                  icon={sec.icon}
                  title={sec.title}
                  badge={sec.badge}
                  badgeTone={sec.badgeTone}
                  body={sec.body}
                  note={sec.note}
                  language={language}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={`shrink-0 border-t bg-white ${isHandheld ? "px-4 py-3" : "px-6 py-3.5 flex justify-end"}`}>
          <div className={isHandheld ? "flex justify-end" : ""}>
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
            >
              {c.close}
            </button>
          </div>
        </div>
      </div>
    </ModalMotionShell>
  );
}
